import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { PublicShell } from "@/components/PublicShell";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, Sparkles, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const HUB_META = {
  cost_guide: {
    label: "Cost Guides",
    heading: "How much does a UK gadget repair really cost?",
    sub: "Honest UK repair-cost bands for every gadget we cover — screen, battery, water damage, charging port and more.",
    urlPrefix: "/cost",
    backLabel: "Back to cost guides",
  },
  comparison: {
    label: "Comparisons",
    heading: "Treasurescare vs everyone else",
    sub: "Independent side-by-side comparisons of premium gadget-protection options in the UK. No affiliate deals — just the numbers.",
    urlPrefix: "/compare",
    backLabel: "Back to comparisons",
  },
  location: {
    label: "By Location",
    heading: "Trusted gadget repair across the UK",
    sub: "Mail-in gadget repair reaching every UK postcode via Royal Mail Tracked 24 pickup. Cover your gadgets from any city.",
    urlPrefix: "/repair",
    backLabel: "Back to locations",
  },
  blog: {
    label: "Guides",
    heading: "Guides for a treasured, protected life",
    sub: "Straight-talking guides for anyone who owns a gadget worth caring about.",
    urlPrefix: "/guide",
    backLabel: "Back to guides",
  },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

// -------- Hub (index) page --------
export function SeoHub({ category }) {
  const [pages, setPages] = useState(null);
  const meta = HUB_META[category] || HUB_META.blog;

  useEffect(() => {
    api.get(`/content/seo/public/${category}`).then((r) => setPages(r.data));
  }, [category]);

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16" data-testid={`seo-hub-${category}`}>
        <div className="max-w-2xl">
          <div className="overline text-secondary">{meta.label}</div>
          <h1 className="mt-2 font-display text-5xl font-light tracking-tight">{meta.heading}</h1>
          <p className="mt-4 text-muted-foreground">{meta.sub}</p>
        </div>

        <div className="mt-12">
          {pages === null && (
            <div className="flex items-center gap-2 text-muted-foreground py-16">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {pages && pages.length === 0 && (
            <div className="text-muted-foreground py-16">Nothing published yet — check back soon.</div>
          )}
          {pages && pages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${category}-list`}>
              {pages.map((p) => (
                <Link key={p.slug}
                      to={`${meta.urlPrefix}/${p.slug}`}
                      data-testid={`${category}-card-${p.slug}`}
                      className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/60 hover:shadow-teal-glow transition-all">
                  <h3 className="font-display text-lg font-medium group-hover:text-primary transition-colors line-clamp-2">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                  )}
                  <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1 group-hover:text-primary">
                    Read <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

// -------- Individual page --------
export function SeoPage({ category }) {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const meta = HUB_META[category] || HUB_META.blog;

  useEffect(() => {
    setPage(null); setNotFound(false);
    api.get(`/content/seo/public/${category}/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true));
  }, [slug, category]);

  // Update <title> + meta description for on-page SEO
  useEffect(() => {
    if (!page) return;
    const prev = document.title;
    document.title = page.meta_title || page.title;
    const existing = document.querySelector('meta[name="description"]');
    const prevDesc = existing?.getAttribute("content");
    if (page.meta_description) {
      if (existing) existing.setAttribute("content", page.meta_description);
      else {
        const el = document.createElement("meta");
        el.name = "description";
        el.content = page.meta_description;
        document.head.appendChild(el);
      }
    }
    return () => {
      document.title = prev;
      if (prevDesc && existing) existing.setAttribute("content", prevDesc);
    };
  }, [page]);

  return (
    <PublicShell>
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16" data-testid={`seo-page-${category}`}>
        <Link to={meta.urlPrefix} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {meta.backLabel}
        </Link>
        {notFound && (
          <div className="mt-8">
            <div className="text-muted-foreground">Page not found.</div>
            <Link to={meta.urlPrefix} className="text-primary text-sm mt-2 inline-block">
              Browse all {meta.label.toLowerCase()} →
            </Link>
          </div>
        )}
        {page === null && !notFound && (
          <div className="mt-8 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {page && (
          <>
            <div className="mt-6 overline text-secondary">{meta.label}</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight leading-tight">
              {page.title}
            </h1>
            {page.excerpt && (
              <p className="mt-4 text-muted-foreground text-lg">{page.excerpt}</p>
            )}
            <div className="mt-3 text-xs text-muted-foreground">
              Published {fmtDate(page.published_at || page.created_at)}
            </div>

            <div
              className="mt-10 prose prose-invert max-w-none text-foreground/90 leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_a]:text-primary [&_a:hover]:underline [&_p]:my-4 [&_table]:my-6 [&_table]:w-full [&_th]:bg-muted/30 [&_th]:p-2 [&_th]:text-left [&_td]:border-t [&_td]:border-border [&_td]:p-2"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.body_html) }}
            />

            <LeadCaptureCTA page={page} category={category} slug={slug} />

            {/* JSON-LD structured data */}
            {page.structured_data && Object.keys(page.structured_data).length > 0 && (
              // eslint-disable-next-line react/no-danger
              <script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: JSON.stringify(page.structured_data),
              }} />
            )}
          </>
        )}
      </article>
    </PublicShell>
  );
}

function LeadCaptureCTA({ page, category, slug }) {
  const [email, setEmail] = useState("");
  const [device, setDevice] = useState(page.page_data?.seed_label || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSubmitting(true);
    try {
      await api.post(`/content/seo/public/${category}/${slug}/lead`, {
        email, device,
        metadata: { source: "seo_page", category, slug, page_id: page.id },
      });
      setDone(true);
      toast.success("Got it — we'll be in touch with your quote.");
    } catch (e) {
      toast.error("Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-12 rounded-3xl bg-primary/10 border border-primary/30 p-8 text-center">
        <div className="text-primary font-medium">Thanks — check your inbox in the next 24h.</div>
        <p className="mt-1 text-sm text-muted-foreground">
          Want cover today? <Link to="/quote" className="text-primary hover:underline">Get an instant quote →</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-12 rounded-3xl bg-primary text-primary-foreground p-8 md:p-10 relative overflow-hidden" data-testid="seo-cta">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="relative max-w-xl">
        <div className="overline text-secondary flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Get a quote
        </div>
        <h2 className="mt-2 font-display text-3xl md:text-4xl font-light">
          See what protection costs for your gadget.
        </h2>
        <p className="mt-2 text-primary-foreground/80 text-sm">
          Drop your email and we'll send a live quote tailored to your device — no signup required.
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-background text-foreground"
            data-testid="seo-cta-email"
          />
          <Button
            type="submit"
            size="lg"
            variant="secondary"
            disabled={submitting}
            className="bg-secondary text-teal hover:bg-secondary/90 rounded-full px-6 shrink-0"
            data-testid="seo-cta-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get quote <Zap className="w-4 h-4 ml-1" /></>}
          </Button>
        </form>
        <p className="mt-3 text-[11px] text-primary-foreground/60">
          We never spam. Unsubscribe in one click.
        </p>
      </div>
    </div>
  );
}
